'use client';

import * as React from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { type BadgeProps, type Radius as DesignSystemRadius } from '@repo/design-system/types';

const badgeVariantsCva = cva(
  // Base classes for the badge
  'inline-flex items-center border font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent text-secondary-foreground bg-white/80 dark:bg-gray-800/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        customRed:
          'premium-gradient-primary text-white border-transparent',
        outline: 'text-foreground',
        // Reminder: These colors should ideally come from design system tokens.
        // Using placeholder Tailwind colors for now.
        success:
          'border-transparent bg-green-500 text-white hover:bg-green-500/80',
        warning:
          'border-transparent bg-yellow-500 text-black hover:bg-yellow-500/80',
        info:
          'border-transparent bg-blue-500 text-white hover:bg-blue-500/80',
        premium:
          'bg-rose-50 text-red-600 border-rose-200 dark:bg-slate-800 dark:text-red-500 dark:border-slate-700',
        premiumShimmer:
          'border-transparent bg-rose-50 text-red-600 dark:bg-slate-800 dark:text-red-500 relative overflow-hidden group',
      },
      size: {
        sm: 'text-xs px-2 py-0.5',
        md: 'text-xs px-2.5 py-0.5',
        lg: 'text-sm px-3 py-1',
        xl: 'text-md px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

// Helper function to map the DesignSystemRadius prop to premium-radius-* CSS classes
const getRadiusClass = (radius?: DesignSystemRadius): string => {
  if (!radius) return 'premium-radius-full'; // Default for badges

  const radiusMapping: Record<DesignSystemRadius, string> = {
    none: 'premium-radius-none',
    sm: 'premium-radius-sm',
    default: 'premium-radius-default',
    md: 'premium-radius-md',
    lg: 'premium-radius-lg',
    xl: 'premium-radius-xl',
    '2xl': 'premium-radius-2xl',
    '3xl': 'premium-radius-3xl',
    full: 'premium-radius-full',
  };
  return radiusMapping[radius] || 'premium-radius-full';
};

export interface WebBadgeProps
  extends Omit<HTMLMotionProps<'div'>, 'variant' | 'size' | 'children'>, // Base on motion props, omit CVA variant names and children
    VariantProps<typeof badgeVariantsCva> { // Add CVA variants (variant, size)
  radius?: DesignSystemRadius; // Explicitly add radius from design system types
  children?: React.ReactNode; // Explicitly type children as React.ReactNode
}

const Badge = React.forwardRef<HTMLDivElement, WebBadgeProps>(
  ({ className, variant, size, radius, children, ...props }, ref) => {
    const radiusClass = getRadiusClass(radius);
    const isPremiumShimmer = variant === 'premiumShimmer';

    const animateConfig = isPremiumShimmer
      ? {
          scale: [1, 1.03, 1],
        }
      : undefined;

    const transitionConfig = isPremiumShimmer
      ? {
          duration: 2,
          repeat: Infinity,
          repeatType: 'mirror' as const,
          ease: 'easeInOut' as const,
        }
      : undefined;

    return (
      <motion.div
        className={cn(badgeVariantsCva({ variant, size }), radiusClass, className)}
        ref={ref}
        animate={animateConfig}
        transition={transitionConfig}
        {...props}
      >
        {children}
        {isPremiumShimmer && (
          <motion.div
            className="absolute inset-0 -z-10 bg-gradient-to-r from-rose-200/10 via-rose-200/50 to-rose-200/10 dark:from-transparent dark:via-white/30 dark:to-transparent"
            animate={{
              x: ['-100%', '200%']
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear' as const,
              delay: 0.5,
            }}
          />
        )}
      </motion.div>
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariantsCva as badgeVariants };
